<template>
  <div class="settlement-detail" data-testid="settlement-detail-page">
    <header class="page-bar">
      <h1>{{ $t('정산 거래 상세') }}</h1>
      <el-button size="small" icon="el-icon-back" @click="$router.push('/v/business/integrated-sales')">
        {{ $t('대시보드') }}
      </el-button>
    </header>

    <section class="filter-band">
      <el-date-picker
        v-model="dateRange"
        size="small"
        type="daterange"
        value-format="yyyy-MM-dd"
        :range-separator="$t('~')"
        :start-placeholder="$t('시작일')"
        :end-placeholder="$t('종료일')">
      </el-date-picker>
      <el-select
        :value="platform"
        size="small"
        clearable
        :placeholder="$t('플랫폼')"
        class="platform-select"
        @change="setPlatform">
        <el-option v-for="p in deliveryPlatformFilters" :key="p" :label="platformDisplayName(p)" :value="p"></el-option>
      </el-select>
      <DeliveryShopSelect :shops="shops" :platform.sync="platform" :shop-no.sync="shopNo" />
      <el-button type="primary" size="small" icon="el-icon-search" @click="loadDetails">{{ $t('검색') }}</el-button>
      <SettlementStatusBadge :badges="badges" />
    </section>

    <SettlementSummaryStrip
      :summary="summary"
      :deductions="deductions"
      :savings="savings"
      @open-savings="savingsDialogVisible = true" />

    <el-alert v-if="errorMessage" type="error" :closable="false" show-icon class="state-alert">
      <span slot="title">{{ errorMessage }}</span>
    </el-alert>

    <div v-if="loading" class="state-panel" aria-busy="true">
      <i class="el-icon-loading"></i>
      <span>{{ $t('정산 상세를 불러오는 중입니다') }}</span>
    </div>

    <section v-else class="detail-insights" aria-label="정산 상세 인사이트">
      <div>
        <span>{{ $t('총 차감') }}</span>
        <strong>{{ formatCurrency(deductionTotal) }}</strong>
      </div>
      <div>
        <span>{{ $t('정산 예상률') }}</span>
        <strong>{{ receiptRate }}%</strong>
      </div>
      <div>
        <span>{{ $t('거래') }}</span>
        <strong>{{ summary.transaction_count || 0 }}{{ $t('건') }}</strong>
      </div>
    </section>

    <SettlementDeductionPanel
      v-if="!loading"
      :deductions="deductions"
      :platform="platform"
      :deduction-sources="details"
      :expected-settlement="summary.expected_settlement || 0"
      :order-amount="summary.order_amount || 0"
      :estimated-fee="summary.estimated_fee || 0"
      class="deduction-panel-spacing" />

    <el-table
      v-if="!loading"
      :data="details"
      size="small"
      border
      stripe
      :empty-text="$t('데이타 없음')"
      class="detail-table">
      <el-table-column :label="$t('일시')" prop="approval_date" min-width="150"></el-table-column>
      <el-table-column :label="$t('매장')" prop="store_name" min-width="150"></el-table-column>
      <el-table-column :label="$t('플랫폼')" min-width="120">
        <template slot-scope="scope">{{
          platformDisplayName(scope.row.delivery_app_tp || scope.row.platform_display_group)
        }}</template>
      </el-table-column>
      <!-- 가게 열은 가게 칸과 같이 N:1 매장이 조회 범위에 있을 때만 — 이름, 없으면 번호, 둘 다 없으면 「-」(옛 매출) -->
      <el-table-column v-if="shops.length" key="shop" :label="$t('가게')" min-width="140" show-overflow-tooltip>
        <template slot-scope="scope">{{ salesShopName(scope.row.shop_name, scope.row.shop_no) || '-' }}</template>
      </el-table-column>
      <el-table-column :label="$t('승인번호')" prop="approval_no" min-width="120"></el-table-column>
      <el-table-column :label="$t('총매출')" prop="order_amount" min-width="110" align="right">
        <template slot-scope="scope">{{ formatCurrency(scope.row.order_amount) }}</template>
      </el-table-column>
      <el-table-column :label="$t('정산 예상')" prop="expected_settlement" min-width="130" align="right">
        <template slot-scope="scope">
          {{ formatCurrency(scope.row.expected_settlement) }}
          <!-- 후불(만나서결제): 정산예상은 현장수취 포함 순수취 기준 — 플랫폼 입금액과 다름을 명시.
               expected_deposit(플랫폼 입금예정)이 있는 행에만 배지를 단다: 미수집 행은 정산예상이
               38% 추정치라 '현장수취 포함 기준' 문구가 성립하지 않고, deposit null 이면 '0원' 오표시된다. -->
          <el-tooltip
            v-if="Number(scope.row.onsite_paid_amount) > 0 && scope.row.expected_deposit != null"
            placement="top">
            <div slot="content">
              {{ $t('후불 주문 — 현장수취') }} {{ formatCurrency(scope.row.onsite_paid_amount) }} {{ $t('포함 기준')
              }}<br />
              {{ $t('플랫폼 입금예정') }} {{ formatCurrency(scope.row.expected_deposit) }}
            </div>
            <el-tag size="mini" type="warning" class="pay-after-tag">{{ $t('후불') }}</el-tag>
          </el-tooltip>
        </template>
      </el-table-column>
      <el-table-column :label="$t('차감')" min-width="110" align="right">
        <template slot-scope="scope">{{ formatCurrency(rowDeduction(scope.row)) }}</template>
      </el-table-column>
      <el-table-column :label="$t('확정일')" prop="settlement_due_date" width="120"></el-table-column>
    </el-table>

    <SettlementSavingsDialog
      :visible.sync="savingsDialogVisible"
      :value="Number(savings.estimatedOrdertalkSavings || 0)" />
  </div>
</template>

<script>
import mainVueMixin from '@/mixins/mainVueMixin'
import { util } from '@/mixins/utilMixin'
import DeliveryShopSelect from '@/components/integratedSales/DeliveryShopSelect.vue'
import SettlementDeductionPanel from '@/components/integratedSales/SettlementDeductionPanel.vue'
import SettlementSavingsDialog from '@/components/integratedSales/SettlementSavingsDialog.vue'
import SettlementStatusBadge from '@/components/integratedSales/SettlementStatusBadge.vue'
import SettlementSummaryStrip from '@/components/integratedSales/SettlementSummaryStrip.vue'
import {
  aggregateDeductionComponents,
  deductionComponentValue,
  deductionTotal as calculateDeductionTotal,
  hasServerDeductionTotal,
  rowPlatform,
} from '@/utils/integratedSalesDeductions'
import { DELIVERY_PLATFORM_FILTERS, platformDisplayName } from '@/utils/deliveryPlatform'
import { salesShopName, shopNoForPlatform } from '@/utils/deliveryShops'

export default {
  mixins: [mainVueMixin],
  components: {
    DeliveryShopSelect,
    SettlementDeductionPanel,
    SettlementSavingsDialog,
    SettlementStatusBadge,
    SettlementSummaryStrip,
  },
  data() {
    return {
      dateRange: [
        this.$route.query.startDate || util.getDate('yyyy-MM-dd', util.dateAdd('d ', -30, new Date())),
        this.$route.query.endDate || util.getNow('yyyy-MM-dd'),
      ],
      platform: this.$route.query.platform || '',
      shopNo: this.$route.query.shopNo || '',
      deliveryPlatformFilters: DELIVERY_PLATFORM_FILTERS,
      summary: {},
      deductions: {},
      details: [],
      badges: {},
      savings: {},
      shops: [],
      loading: false,
      errorMessage: '',
      savingsDialogVisible: false,
    }
  },
  computed: {
    deductionTotal() {
      // 차감 합계도 수수료 미수집 38% 추정을 포함해 정산예상과 정합(매출 − 총차감 = 정산예상)
      return this.realDeductionTotal + Number(this.summary.estimated_fee || 0)
    },
    realDeductionTotal() {
      if (hasServerDeductionTotal(this.deductions)) {
        return Number(this.deductions.deduction_amount || 0)
      }
      if (!this.platform && this.details.length) {
        return Object.values(aggregateDeductionComponents(this.details)).reduce(
          (sum, value) => sum + Number(value || 0),
          0
        )
      }
      return (
        deductionComponentValue(this.deductions, 'delivery_fee', this.platform, false) +
        deductionComponentValue(this.deductions, 'store_delivery_tip', this.platform, false) +
        Number(this.deductions.service_commission || 0) +
        Number(this.deductions.card_commission || 0) +
        deductionComponentValue(this.deductions, 'platform_discount', this.platform, false) +
        Number(this.deductions.store_discount || 0) +
        Number(this.deductions.vat || 0) +
        Number(this.deductions.ad_cost || 0) +
        Number(this.deductions.instant_discount || 0)
      )
    },
    receiptRate() {
      const sales = Number(this.summary.order_amount || 0)
      if (!sales) return '0.0'
      return ((Number(this.summary.expected_settlement || 0) / sales) * 100).toFixed(1)
    },
  },
  created() {
    this.loadDetails()
  },
  methods: {
    platformDisplayName,
    salesShopName,
    /** 플랫폼 칸 — 가게는 제 앱에서만 남는다(`shopNoForPlatform`) */
    setPlatform(platform) {
      this.shopNo = shopNoForPlatform({ platform: this.platform, shopNo: this.shopNo }, platform)
      this.platform = platform
    },
    async loadDetails() {
      this.loading = true
      this.errorMessage = ''
      try {
        const outcome = await this.mainVue.$axios.search(
          '/api/statistics/integrated-sales/settlement-detail',
          {
            startDate: this.dateRange?.[0],
            endDate: this.dateRange?.[1],
            platform: this.platform || undefined,
            shopNo: this.shopNo || undefined,
          },
          (result) => {
            this.summary = result.summary || {}
            this.deductions = result.deductions || {}
            this.details = result.details || []
            this.badges = result.badges || {}
            this.savings = result.savings || {}
            this.shops = result.shops || []
          },
          false
        )
        // 요청 실패는 헬퍼가 값으로 알린다 — 아래 catch 한 곳이 안내한다
        if (!outcome.ok) throw outcome.error
      } catch (error) {
        this.errorMessage = this.$t('정산 상세를 불러오지 못했습니다. 잠시 후 다시 시도해주세요.')
      } finally {
        this.loading = false
      }
    },
    rowDeduction(row) {
      return calculateDeductionTotal(row, rowPlatform(row, this.platform)) + Number(row.estimated_fee || 0)
    },
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
  },
}
</script>

<style scoped>
.settlement-detail {
  min-height: calc(100vh - 120px);
  padding: 16px;
  background: var(--color-bg-page);
}

.page-bar,
.filter-band {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 12px;
}

h1 {
  margin: 0;
  color: var(--color-text-primary);
  font-size: 18px;
  font-weight: 700;
}

.filter-band {
  justify-content: flex-start;
  padding: 12px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-card);
}

.platform-select {
  width: 150px;
}

.detail-insights {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
  margin: 12px 0;
}

.state-alert {
  margin-bottom: 12px;
}

.state-panel {
  display: flex;
  min-height: 140px;
  align-items: center;
  justify-content: center;
  gap: 10px;
  margin: 12px 0;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-regular);
}

.detail-insights div {
  display: flex;
  min-height: 56px;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 12px 14px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  color: var(--color-text-regular);
}

.detail-insights strong {
  color: var(--color-text-primary);
  font-size: 18px;
}

.deduction-panel-spacing {
  margin-bottom: 12px;
}

.detail-table {
  min-width: 0;
  border-radius: var(--radius-md);
  overflow: hidden;
}

.pay-after-tag {
  margin-left: 4px;
  vertical-align: middle;
}

@media (max-width: 768px) {
  .settlement-detail {
    padding: 10px;
  }

  .filter-band .el-date-editor,
  .platform-select,
  .filter-band .shop-select,
  .filter-band .el-button {
    width: 100%;
  }

  .detail-insights {
    grid-template-columns: 1fr;
  }
}
</style>
